import React, { useCallback, useEffect, useRef, useState } from "react";
import { useStore } from "@/stores/useStore";
import { useToast } from "@/hooks/useToast";
import { useRoPanelParams } from "@/hooks/useRoPanelParams";
import { useToastSound } from "@/hooks/useToastSound";
import { resolveToastFields } from "@/lib/toastFields";
import {
  ActiveToast,
  ownsToastSlot,
  planAutomationToastCycle,
  pruneExpiredSeenToastKeys,
} from "@/lib/automationToastPlan";
import { canClaimToastSlot, TOAST_PRIORITY } from "@/lib/toastEvictionCooldown";
import {
  AUTOMATION_TOAST_AUTO_DISMISS_MS,
  AUTOMATION_TOAST_VISIBLE_LIMIT,
} from "@/components/Base/AutomationToastStack";
import { hydrateInto, persistSeenToasts } from "@/lib/seenToastStore";

const TICK_MS = 30_000;

// Module scope, not a useRef: AutomationToastManager mounts inside
// DashboardContainer, which unmounts on route change (e.g. open Settings,
// come back). A component-scoped ref would reset on remount, and because a
// toast's eligibility window is wall-clock (TOAST_ELIGIBILITY_WINDOW_MS, 5
// minutes — see automationShowToast.ts), a toast shown and dismissed at
// 10:00 would replay in full at 10:02 after that round trip: the payload's
// expiresAt (10:05) hasn't passed, and a fresh per-instance seen-set has no
// memory of ever having shown it. Module scope survives remounts for as
// long as the tab stays open; it does not survive a reload, which replayed
// toasts inside the 5-minute eligibility window (WEBV3-177).
// hydrateSeenToasts/persistSeenToasts below back these structures with
// localStorage — not sessionStorage, because these payloads are shop-wide
// broadcasts and once-per-browser is the right grain for a shared shop
// terminal, whereas sessionStorage would replay in every new tab. (Tabs
// already open when a toast fires still each show it: there is no `storage`
// event listener, and each tab hydrates once at load.)
// Persisting deliberately does NOT clear card.toastNotifications: the
// payload is a broadcast, so consuming it would deprive every other viewer
// of it.
//
// Two structures rather than one Map<string, number>: seenToastKeys is what
// selectPendingToasts checks, kept as a plain Set<string> to match its
// existing signature exactly (no ripple into toastNotificationSelectors.ts);
// seenToastExpiry is a side table of key -> expiresAt(ms), recorded purely so
// pruneExpiredSeenToastKeys can bound their combined memory over a
// long-running tab. markToastsSeen below keeps the two in sync.
const seenToastKeys = new Set<string>();
const seenToastExpiry = new Map<string, number>();

// A third structure, and the only one that is ever written to localStorage:
// the subset of seenToastExpiry that actually REACHED THE SCREEN.
//
// "Shown" and "seen by a human" are not the same event here. The shared slot
// holds one toast (TOAST_LIMIT=1) and ADD_TOAST slices, so a higher-priority
// manager (MyOverdueTaskToastManager, MyCustomerUpdateToastManager) is
// entitled to evict this manager's toast milliseconds after it appears. Both
// now check occupancy via canClaimToastSlot and dispatch() now notifies the
// evicted toast's owner, but neither changes the outcome for an automation
// toast: it is the lowest priority, so it can still be legitimately preempted
// before anyone reads it. In-memory that
// was survivable: the firing was lost for the life of the tab, and a reload
// recovered it, because a reload started from an empty seen-set. Persisting
// at show time would remove that last recovery path and make the silent loss
// permanent — the exact failure this feature must never produce, since it is
// far worse to suppress a toast nobody saw than to show one twice.
//
// So: mark seen at show time (in memory, to stop it re-toasting this
// session), but record it durably only once the slot is confirmed to have
// still been ours at dismissal. See dismissActive.
const deliveredToastExpiry = new Map<string, number>();

// Read lazily on the first cycle rather than at module import, so that
// importing this module in a test or any non-browser context has no side
// effect.
let seenToastsHydrated = false;

/**
 * Seeds the module-scoped seen-toast bookkeeping from localStorage, once per
 * page load. Module scope alone survives this manager's remounts but not a
 * reload — and because a payload stays eligible for TOAST_ELIGIBILITY_WINDOW_MS
 * (5 minutes) on the card, a reload inside that window would otherwise replay
 * a toast the user already saw and dismissed (WEBV3-177).
 * @param {Date} now - Current wall-clock time; entries past their expiry are dropped.
 */
function hydrateSeenToasts(now: Date): void {
  if (seenToastsHydrated) return;
  seenToastsHydrated = true;
  hydrateInto(seenToastKeys, seenToastExpiry, now);
  // Anything that was persisted was, by definition, already delivered on some
  // earlier page load. Seed the delivered map from the same source so the
  // next write — which replaces the whole stored blob — doesn't drop it.
  // Read from storage again rather than copying seenToastExpiry: copying
  // would be correct only while nothing has been marked seen yet, making this
  // depend on statement ordering elsewhere in the effect. The throwaway Set
  // is because only the expiry map is wanted here.
  hydrateInto(new Set<string>(), deliveredToastExpiry, now);
}

/**
 * Records the toasts that were on screen at dismissal as durably delivered.
 * @param {ActiveToast[]} active - The accumulated set being dismissed.
 */
function markToastsDelivered(active: ActiveToast[]): void {
  let changed = false;
  active.forEach((toast) => {
    const expiresAtMs = toast.expiresAt.getTime();
    if (deliveredToastExpiry.get(toast.key) !== expiresAtMs) {
      deliveredToastExpiry.set(toast.key, expiresAtMs);
      changed = true;
    }
  });
  if (changed) persistSeenToasts(deliveredToastExpiry);
}

/**
 * Marks freshly pending toast keys seen and records each one's expiresAt
 * for later pruning.
 * @param {string[]} keys - Keys to mark seen this cycle (a plan's `newKeys`).
 * @param {ActiveToast[]} active - The plan's full accumulated set (its
 *   `active`), used to look up each key's expiresAt.
 */
function markToastsSeen(keys: string[], active: ActiveToast[]) {
  if (keys.length === 0) return;
  const expiryByKey = new Map<string, number>(
    active.map((a): [string, number] => [a.key, a.expiresAt.getTime()])
  );
  keys.forEach((key) => {
    seenToastKeys.add(key);
    const expiresAtMs = expiryByKey.get(key);
    if (expiresAtMs !== undefined) seenToastExpiry.set(key, expiresAtMs);
  });
}

export const AutomationToastManager: React.FC = () => {
  const cards = useStore((state) => state.cards);
  const shop = useStore((state) => state.shop);
  const employees = useStore((state) => state.employees);
  const employeesLoaded = useStore((state) => state.employeesLoaded);
  const { showAutomationToasts, toasts } = useToast();
  const { selectCard } = useRoPanelParams();
  const { playToastSound } = useToastSound();

  const [now, setNow] = useState<Date>(() => new Date());
  const currentToastRef = useRef<{ id: string; dismiss: () => void } | null>(
    null
  );
  const dismissTimerRef = useRef<number | null>(null);
  // Toasts currently represented on screen (visible rows + overflow count).
  // See planAutomationToastCycle for why this accumulator — not another pass
  // over selectPendingToasts — is what keeps an already-shown toast alive
  // across the many unrelated `cards` updates that happen while it's on
  // screen, and what lets concurrent firings stack instead of replacing.
  const activeRef = useRef<ActiveToast[]>([]);
  // Mirrors `toasts` so dismissActive can read the slot's current occupant
  // without taking `toasts` as a dependency — its identity is the unmount
  // guard below, and re-creating it on every toast-store change would fire a
  // dismissal on each one.
  const toastsRef = useRef(toasts);
  toastsRef.current = toasts;
  // Whether the tab was in the foreground at the moment the current toast was
  // shown. Checked instead of visibility at dismissal time: a toast that
  // rendered into a hidden tab was never seen, but one the user read before
  // switching away was — and dismissal-time visibility can't tell them apart.
  const shownWhileVisibleRef = useRef(false);

  // Expiry is wall-clock, not write-driven, so re-evaluate on an interval.
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), TICK_MS);
    return () => window.clearInterval(id);
  }, []);

  // Records the on-screen toasts as durably delivered, if they really did
  // reach the screen. Called from every dismissal route and from `pagehide`,
  // which is what covers a reload while the toast is still up.
  const recordDeliveryIfOnScreen = useCallback(() => {
    const onScreen = ownsToastSlot(
      toastsRef.current[0]?.id ?? null,
      currentToastRef.current?.id ?? null
    );
    if (onScreen && shownWhileVisibleRef.current) {
      markToastsDelivered(activeRef.current);
    }
  }, []);

  const dismissActive = useCallback(() => {
    if (dismissTimerRef.current !== null) {
      window.clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = null;
    }
    // Delivery is confirmed here, not at show time: reaching this point with
    // the slot still holding our own toast means it survived on screen until
    // something dismissed it — a click, the auto-dismiss timer, an updated
    // stack replacing it, or leaving the dashboard. If a foreign toast holds
    // the slot (or it is empty), ours was sliced away unseen and must stay
    // recoverable by a reload, so nothing is written.
    recordDeliveryIfOnScreen();
    currentToastRef.current?.dismiss();
    currentToastRef.current = null;
    // Clear the accumulator on every dismissal (manual or auto) — otherwise
    // the next unrelated `cards` update would immediately re-show whatever
    // the user just closed (its keys are still in `seenToastKeys`, so only
    // the accumulator was keeping it alive).
    activeRef.current = [];
  }, [recordDeliveryIfOnScreen]);

  // Dismiss on unmount (leaving the dashboard).
  useEffect(() => dismissActive, [dismissActive]);

  // A reload or tab close while the toast is still on screen is a delivery
  // too — and it is the single most likely way a user meets this feature
  // ("the toast is up, I hit refresh"). React runs no cleanup on unload, so
  // without this the toast would replay, which is the original bug. `pagehide`
  // rather than `beforeunload`: it fires reliably, including on mobile and on
  // back/forward-cache navigations. No visibility check here — the tab is
  // already `hidden` by the time `pagehide` runs, so `shownWhileVisibleRef`
  // inside recordDeliveryIfOnScreen is what carries that judgement.
  useEffect(() => {
    window.addEventListener("pagehide", recordDeliveryIfOnScreen);
    return () =>
      window.removeEventListener("pagehide", recordDeliveryIfOnScreen);
  }, [recordDeliveryIfOnScreen]);

  useEffect(() => {
    // Wait for employees to actually load — not just be present — before
    // resolving a cycle. `employees` seeds as `[]` before the subscription's
    // first snapshot, so gating on it directly can't tell "not loaded yet"
    // apart from "shop genuinely has zero employees". Because a resolved
    // toast is marked seen immediately below, resolving too early against an
    // empty array would drop a Technician/Service Advisor field to "" and
    // that degraded render would be permanent for this firing. `employeesLoaded`
    // (set true only after the first snapshot) makes the distinction: a
    // zero-employee shop still flips it true and toasts proceed normally.
    if (!shop || !employeesLoaded) return;

    // Placement is load-bearing: after the shop/employees gate (nothing is
    // shown before it anyway) but before the first planAutomationToastCycle
    // call can read the seen-set.
    hydrateSeenToasts(now);

    // Prune both maps on the same predicate: seenToastExpiry bounds memory,
    // deliveredToastExpiry bounds what gets written back. Passing
    // seenToastKeys to both calls is intentional — delivered keys are a
    // subset of seen keys, so the second call's set deletions are a no-op.
    // Only a change to the delivered map warrants a write.
    pruneExpiredSeenToastKeys(seenToastKeys, seenToastExpiry, now);
    if (pruneExpiredSeenToastKeys(seenToastKeys, deliveredToastExpiry, now)) {
      persistSeenToasts(deliveredToastExpiry);
    }

    // Free-or-ours: the shared toast slot may currently hold nothing, our
    // own previously-shown automation toast (fine — the "show" branch below
    // dismisses and replaces it with an updated stack), or a toast some
    // *other* manager owns (a customer update mid-edit, an overdue-task
    // toast a user is reading). Only the last case must defer: showing now
    // would slice that toast off the screen mid-read. dispatch() would notify
    // its owner via onOpenChange(false, "evicted") so the loss is at least not
    // silent, but not evicting it in the first place is the point. Automation
    // sits at the bottom of TOAST_PRIORITY (tied with untagged toasts), so
    // this defers to every occupant it doesn't already own. See
    // canClaimToastSlot for why this must compare ids rather than just
    // checking occupancy.
    const slotFreeOrOurs = canClaimToastSlot({
      toasts,
      myPriority: TOAST_PRIORITY.AUTOMATION,
      myToastId: currentToastRef.current?.id ?? null,
    });

    const plan = planAutomationToastCycle(
      cards,
      now,
      seenToastKeys,
      activeRef.current,
      AUTOMATION_TOAST_VISIBLE_LIMIT,
      slotFreeOrOurs
    );

    if (plan.type === "none" || plan.type === "defer") return;

    if (plan.type === "dismiss") {
      dismissActive();
      return;
    }

    // plan.type === "show"
    //
    // Mark every freshly pending toast seen immediately — including
    // whatever ends up in the overflow count. Without this the next tick
    // would re-toast it. This is independent of the accumulator: seenKeys
    // is what stops the selector from offering a toast again; `active` is
    // what keeps it visible in the meantime.
    markToastsSeen(plan.newKeys, plan.active);

    const items = plan.visible.map((p) => {
      const card = cards.find((c) => c.id === p.cardId);
      return {
        key: p.key,
        cardId: p.cardId,
        headline: p.headline,
        lines: card
          ? resolveToastFields(card, shop, employees ?? [], p.fields)
          : [],
      };
    });

    dismissActive();
    activeRef.current = plan.active;
    // Snapshot foreground-ness at the moment of showing — see the ref's
    // declaration for why this beats checking visibility at dismissal.
    shownWhileVisibleRef.current = document.visibilityState !== "hidden";
    currentToastRef.current = showAutomationToasts({
      items,
      overflow: plan.overflow,
      onOpenCard: (cardId) => {
        selectCard(cardId);
        dismissActive();
      },
      onDismiss: () => dismissActive(),
    });

    // Sound only for a genuinely new arrival. A "show" with no new keys is a
    // row aging out of the stack — nothing new reached the user. A deferred
    // cycle never gets here (its plan type is "defer"), so a toast held back
    // by a higher-priority one dings when it is really shown, not before.
    // Rate limiting (one per second) lives in useToastSound. Each new toast's
    // own category picks which sound switch applies.
    if (plan.newKeys.length > 0) {
      // One ask per distinct category among the toasts that just arrived. The
      // first enabled one dings; the rest are dropped by the one-per-second
      // limit, and a muted category returns before touching that limit.
      const newKeys = new Set(plan.newKeys);
      const categories = new Set(
        plan.active.filter((t) => newKeys.has(t.key)).map((t) => t.category)
      );
      categories.forEach((category) => playToastSound(category));
    }

    // Any change to the displayed set — a newly arrived item or one aging
    // out — re-arms the full auto-dismiss window, so a freshly stacked row
    // always gets the full ~10s to be read. This timer must be held in a
    // ref, not returned as this effect's cleanup: the effect re-runs on
    // every `cards` change, which on a live board is constant. A
    // cleanup-based timer would be cleared by the very next unrelated
    // update, and because the re-run above returns `{ type: "none" }` once
    // nothing has changed, no replacement timer would ever be set — the
    // toast would hang on screen forever. The ref-held timer survives
    // re-runs and is cleared only by an actual dismissal (dismissActive) or
    // unmount.
    dismissTimerRef.current = window.setTimeout(
      dismissActive,
      AUTOMATION_TOAST_AUTO_DISMISS_MS
    );
  }, [
    cards,
    shop,
    employees,
    employeesLoaded,
    now,
    showAutomationToasts,
    playToastSound,
    selectCard,
    dismissActive,
    toasts,
  ]);

  return null;
};
